import React, { useRef, useState } from 'react';
import { Input, Modal } from 'antd';
import type { InputRef } from 'antd';
import type { MapFeature } from '../types';

// ─────────────────────────────────────────────────────────────
// PaddockRenameModal
// Centred modal for renaming a paddock.
//   • Pre-fills the current name
//   • Positions cursor at the end on open (ready for backspace)
//   • allowClear — clear icon clears the field
//   • Validates non-empty on submit
// Phase 1: onSave is synchronous (local state only, no API call).
// ─────────────────────────────────────────────────────────────

interface Props {
  paddock: MapFeature;
  onSave: (newName: string) => void;
  onCancel: () => void;
}

const PaddockRenameModal = ({ paddock, onSave, onCancel }: Props) => {
  const [name, setName] = useState(paddock.name);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<InputRef>(null);

  const handleAfterOpen = (open: boolean) => {
    if (!open) return;
    const el = inputRef.current?.input;
    if (el) {
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (error) setError(null);
  };

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Paddock name cannot be empty.');
      return;
    }
    onSave(trimmed);
  };

  return (
    <Modal
      open
      title="Rename Paddock"
      okText="Save"
      cancelText="Cancel"
      onOk={handleSave}
      onCancel={onCancel}
      mask={{ closable: false }}
      destroyOnHidden
      afterOpenChange={handleAfterOpen}
    >
      <div className="mt-2">
        <Input
          ref={inputRef}
          value={name}
          onChange={handleChange}
          onPressEnter={handleSave}
          allowClear
          status={error ? 'error' : undefined}
          placeholder="Paddock name"
        />
        {error && (
          <p className="mt-1.5 text-xs text-red-500">{error}</p>
        )}
      </div>
    </Modal>
  );
};

export default PaddockRenameModal;
